import { useFormikContext } from "formik";
import { Flex } from "@radix-ui/themes";
import { object, string, InferType } from "yup";
import InputField from "@/components/form/InputField";
import SelectField from "@/components/form/SelectField";
import { WorkflowStep } from "@/types/breeze/Workflow";
import VariableInputField from "./shared/VariableInputField";

export const adjustBalanceValidationSchema = object({
  parameters: object({
    user_id: string().required("User id is required"),
    delta: string().required("Amount is required"),
    unit_type: string(),
    currency: string(),
    source: string(),
    source_ref: string(),
    reason: string(),
  }),
});

export type AdjustBalanceFormValues = InferType<typeof adjustBalanceValidationSchema>;

export default function AdjustBalanceFields({
  existingSteps = [],
  triggerEntity,
}: {
  existingSteps?: WorkflowStep[];
  triggerEntity?: string;
}) {
  const { values } = useFormikContext<AdjustBalanceFormValues>();
  const variableProps = { existingSteps, triggerEntity };

  return (
    <Flex direction="column" gap="2">
      <VariableInputField
        name="parameters.user_id"
        label="User id"
        placeholder="{{ $anythink.trigger.data.user_id }}"
        required
        {...variableProps}
      />
      <VariableInputField
        name="parameters.delta"
        label="Amount"
        placeholder="{{ $anythink.trigger.data.points }}"
        description="A whole number. Negative takes away. Credit is in pence or cents."
        required
        {...variableProps}
      />
      <SelectField name="parameters.unit_type" label="Unit" type="string">
        <SelectField.Item value="points">Points</SelectField.Item>
        <SelectField.Item value="credit">Credit</SelectField.Item>
      </SelectField>
      {values.parameters?.unit_type === "credit" && (
        <InputField name="parameters.currency" label="Currency" options={{ placeholder: "gbp" }} />
      )}
      <InputField
        name="parameters.source"
        label="Source"
        description="Where the points came from, shown in Growth > Balances. Defaults to manual_admin."
        options={{ placeholder: "workout" }}
      />
      <VariableInputField
        name="parameters.source_ref"
        label="Source reference"
        placeholder="{{ $anythink.trigger.data.id }}"
        description="A repeat with the same source and reference adds nothing, so a re-run never pays twice."
        {...variableProps}
      />
      <VariableInputField name="parameters.reason" label="Reason" {...variableProps} />
    </Flex>
  );
}
